import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SectionHeading } from "@/components/PageShell";
import { inputClass, labelClass } from "@/pages/EnquiryPage";

const TIERS = [
  { amount: 25000, label: "£25,000", note: "A season of short-haul flying" },
  { amount: 50000, label: "£50,000", note: "Regular European charter", featured: true },
  { amount: 100000, label: "£100,000", note: "Long-range and ultra-long-range" },
];

const POINTS = [
  "No membership fee required and no flight commitment",
  "Each charter is quoted, approved by you, then deducted from your balance",
  "No hourly caps imposed by us — you pay the price we source, nothing more",
  "Unused balance stays on your account; statements after every flight",
  "Fund securely by card via Stripe; top up any time",
];

export const JetAccount = () => {
  const [open, setOpen] = useState(null);
  return (
    <section id="jet-account" data-testid="jet-account-section" className="py-24 lg:py-32 bg-surface border-t border-white/5 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="lg:col-span-5">
            <SectionHeading overline="The Aero Vista Jet Account" title="Pre-fund once. Fly on demand.">
              A flight balance held on account with the Club. Tell Marcelle where and when; we source the aircraft, you approve the quote, and it is simply drawn from your balance.
            </SectionHeading>
            <ul className="space-y-3 -mt-6">
              {POINTS.map((b) => (<li key={b} className="flex items-start gap-3 text-sm text-white/70"><Check size={14} className="text-gold mt-1 shrink-0" />{b}</li>))}
            </ul>
          </motion.div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {TIERS.map((t, i) => (
                <motion.button key={t.amount} type="button" onClick={() => setOpen(t.amount)} data-testid={`jet-account-tier-${t.amount}`}
                               initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
                               className={`relative text-left p-7 rounded-sm border transition-colors duration-300 hover:border-gold ${t.featured ? "bg-obsidian border-gold/50" : "bg-obsidian border-white/10"}`}>
                  {t.featured && <span className="absolute -top-3 left-6 text-[10px] uppercase tracking-[0.25em] bg-gold text-black px-3 py-1">Most chosen</span>}
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Fund</p>
                  <p className="font-serif text-3xl text-white mt-3">{t.label}</p>
                  <p className="text-xs text-white/50 mt-2">{t.note}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-gold">Open account <ArrowRight size={12} /></span>
                </motion.button>
              ))}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-white/10 rounded-sm p-6 bg-obsidian">
              <p className="text-sm text-white/60">Prefer a different amount? Accounts open from £10,000.</p>
              <button type="button" onClick={() => setOpen("custom")} data-testid="jet-account-custom-button" className="text-[11px] uppercase tracking-[0.2em] text-white/80 hover:text-gold transition-colors">Choose my own amount →</button>
            </div>
            <p className="mt-4 text-[11px] text-white/30">Funds are held on account for charter services arranged by Aero Vista Club and applied only to quotes you approve. Payment by Stripe; tax calculated at checkout where applicable.</p>
          </div>
        </div>
      </div>
      {open && <FundDialog preset={open} onClose={() => setOpen(null)} />}
    </section>
  );
};

const FundDialog = ({ preset, onClose }) => {
  const [form, setForm] = useState({ amount: preset === "custom" ? "" : String(preset), name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api("/jet-account/fund", { method: "POST", body: { amount: Number(form.amount), origin_url: window.location.origin, name: form.name, email: form.email, phone: form.phone || null } });
      window.location.href = r.checkout_url;
    } catch (err) { toast.error(err.message); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="jet-account-dialog">
      <form onSubmit={submit} className="w-full max-w-md bg-surface border border-white/10 rounded-sm p-8 relative">
        <button type="button" onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-white/50 hover:text-white" data-testid="jet-account-dialog-close"><X size={18} /></button>
        <p className="text-xs uppercase tracking-[0.25em] text-gold">Open your Jet Account</p>
        <h3 className="font-serif text-3xl text-white mt-3">Fund your flight balance</h3>
        <div className="mt-6 space-y-4">
          <div><label className={labelClass}>Amount (GBP, from 10,000)</label><input required type="number" min="10000" step="500" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputClass} data-testid="jet-account-amount-input" /></div>
          <div><label className={labelClass}>Name</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} data-testid="jet-account-name-input" placeholder="Your full name" /></div>
          <div><label className={labelClass}>Email</label><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} data-testid="jet-account-email-input" placeholder="you@company.com" /></div>
          <div><label className={labelClass}>Phone</label><input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputClass} data-testid="jet-account-phone-input" placeholder="+44 ..." /></div>
        </div>
        <button type="submit" disabled={busy} data-testid="jet-account-checkout-button" className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.2em] py-4 rounded-sm hover:bg-gold-light disabled:opacity-50 transition-colors">{busy ? "Redirecting to secure checkout..." : "Continue to secure payment"}</button>
      </form>
    </div>
  );
};
